import React, { useState, useEffect, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";

import { i18n } from "../../translate/i18n";
import {
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import { Field, Form, Formik } from "formik";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

import axios from "axios";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import PageHelpGuide from "../../components/PageHelpGuide";
import { MESSAGES_API_HELP_DATA } from "../../constants/pageHelp/messagesApiHelp";
import { getStandardButtonStyles } from "../../styles/standardButtons";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    paddingBottom: 100,
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    borderRadius: 16,
    backgroundColor: theme.palette.type === 'dark' ? '#2A2D32 !important' : theme.palette.background.paper,
    ...theme.scrollbarStyles,
  },
  contentWrapper: {
    padding: theme.spacing(2, 3),
    display: "flex",
    flexDirection: "column",
  },
  mainHeader: {
    marginTop: theme.spacing(1),
  },
  elementMargin: {
    padding: theme.spacing(1.5, 0),
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  formContainer: {
    maxWidth: 500,
  },
  textRight: {
    textAlign: "right",
  },
  list: {
    paddingLeft: theme.spacing(2),
  },
}));

const MessagesAPI = () => {
  const classes = useStyles();
  const history = useHistory();
  const mainPaperRef = useRef(null);

  useEffect(() => {
    if (mainPaperRef.current) {
      mainPaperRef.current.scrollTop = 0;
    }
    window.scrollTo(0, 0);
  }, []);

  const [formMessageTextData] = useState({
    token: "",
    number: "",
    body: "",
    userId: "",
    queueId: "",
  });
  const [formMessageMediaData] = useState({
    token: "",
    number: "",
    medias: "",
    body: "",
    userId: "",
    queueId: "",
  });
  const [file, setFile] = useState({});
  const { user } = useContext(AuthContext);

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useExternalApi) {
        toast.error(
          i18n.t("toastError.permissionDenied")
        );
        setTimeout(() => {
          history.push(`/`);
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getEndpoint = () => {
    return process.env.REACT_APP_BACKEND_URL + "/api/messages/send";
  };

  const handleSendTextMessage = async (values) => {
    const { number, body, userId, queueId } = values;
    const data = { number, body, userId, queueId };
    try {
      await axios.request({
        url: getEndpoint(),
        method: "POST",
        data,
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${values.token}`,
        },
      });
      toast.success(i18n.t("messages.success.sent"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleSendMediaMessage = async (values) => {
    try {
      const firstFile = file[0];
      const data = new FormData();
      data.append("number", values.number);
      data.append("body", values.body ? values.body : firstFile.name);
      data.append("userId", values.userId);
      data.append("queueId", values.queueId);
      data.append("medias", firstFile);
      await axios.request({
        url: getEndpoint(),
        method: "POST",
        data,
        headers: {
          "Content-type": "multipart/form-data",
          Authorization: `Bearer ${values.token}`,
        },
      });
      toast.success(i18n.t("messages.success.sent"));
    } catch (err) {
      toastError(err);
    }
  };

  const renderFormMessageText = () => {
    return (
      <Formik
        initialValues={formMessageTextData}
        enableReinitialize={true}
        onSubmit={(values, actions) => {
          setTimeout(async () => {
            await handleSendTextMessage(values);
            actions.setSubmitting(false);
            actions.resetForm();
          }, 400);
        }}
        className={classes.elementMargin}
      >
        {({ isSubmitting }) => (
          <Form className={classes.formContainer}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.token")}
                  name="token"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.number")}
                  name="number"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.body")}
                  name="body"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.userId")}
                  name="userId"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.queueId")}
                  name="queueId"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={12} className={classes.textRight}>
                <Button
                  type="submit"
                  className={classes.applyButton}
                >
                  {isSubmitting ? (
                    <CircularProgress
                      size={20}
                      style={{ color: "#FFF" }}
                    />
                  ) : (
                    <>{i18n.t("messagesAPI.toSend")}</>
                  )}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    );
  };

  const renderFormMessageMedia = () => {
    return (
      <Formik
        initialValues={formMessageMediaData}
        enableReinitialize={true}
        onSubmit={(values, actions) => {
          setTimeout(async () => {
            await handleSendMediaMessage(values);
            actions.setSubmitting(false);
            actions.resetForm();
            document.getElementById("medias").files = null;
            document.getElementById("medias").value = null;
          }, 400);
        }}
        className={classes.elementMargin}
      >
        {({ isSubmitting }) => (
          <Form className={classes.formContainer}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.mediaMessage.token")}
                  name="token"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.mediaMessage.number")}
                  name="number"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.body")}
                  name="body"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.userId")}
                  name="userId"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.queueId")}
                  name="queueId"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={12}>
                <input
                  type="file"
                  name="medias"
                  id="medias"
                  required
                  onChange={(e) => setFile(e.target.files)}
                />
              </Grid>
              <Grid item xs={12} className={classes.textRight}>
                <Button
                  type="submit"
                  className={classes.applyButton}
                >
                  {isSubmitting ? (
                    <CircularProgress
                      size={20}
                      style={{ color: "#FFF" }}
                    />
                  ) : (
                    <>{i18n.t("messagesAPI.toSend")}</>
                  )}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    );
  };

  return (
    <MainContainer>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        ref={mainPaperRef}
      >
        <MainHeader>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <PageHelpGuide
              helpData={MESSAGES_API_HELP_DATA}
              tooltipText="Guía y Ayuda de API de Mensajes (Haz clic para ver)"
            />
            <Title>{i18n.t("messagesAPI.API.title")}</Title>
          </div>
        </MainHeader>

        <div className={classes.contentWrapper}>
          <Typography
            variant="h6"
            color="primary"
            className={classes.sectionTitle}
          >
            {i18n.t("messagesAPI.API.methods.title")}
          </Typography>
          <Typography component="div" className={classes.elementMargin}>
            <ol className={classes.list}>
              <li>{i18n.t("messagesAPI.API.methods.messagesText")}</li>
              <li>{i18n.t("messagesAPI.API.methods.messagesMedia")}</li>
            </ol>
          </Typography>
          <Typography
            variant="h6"
            color="primary"
            className={classes.sectionTitle}
          >
            {i18n.t("messagesAPI.API.instructions.title")}
          </Typography>
          <Typography component="div" className={classes.elementMargin}>
            <b>{i18n.t("messagesAPI.API.instructions.comments")}</b>
            <br />
            <ul className={classes.list}>
              <li dangerouslySetInnerHTML={{ __html: i18n.t("messagesAPI.API.instructions.comments1") }} />
              <li>
                <span dangerouslySetInnerHTML={{ __html: i18n.t("messagesAPI.API.instructions.comments2") }} />
                <ul style={{ marginTop: "30px" }}>
                  <li>{i18n.t("messagesAPI.API.instructions.codeCountry")}</li>
                  <li>{i18n.t("messagesAPI.API.instructions.code")}</li>
                  <li>{i18n.t("messagesAPI.API.instructions.number")}</li>
                </ul>
              </li>
            </ul>
          </Typography>
          <Typography
            variant="h6"
            color="primary"
            className={classes.sectionTitle}
          >
            {i18n.t("messagesAPI.API.text.title")}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography component="div" className={classes.elementMargin}>
                <p>{i18n.t("messagesAPI.API.text.instructions")}</p>
                <b>
                  {i18n.t("messagesAPIInstructions.endpoint")}
                </b> {getEndpoint()} <br />
                <b>{i18n.t("messagesAPIInstructions.method")}</b>{" "}
                {i18n.t("messagesAPIInstructions.post")} <br />
                <b>{i18n.t("messagesAPIInstructions.headers")}</b>{" "}
                {i18n.t("messagesAPIInstructions.headerAuthorization")} <br />
                <b>{i18n.t("messagesAPIInstructions.body")}</b> {"{"} <br />
                {i18n.t("messagesAPIInstructions.fieldNumber")} <br />
                {i18n.t("messagesAPIInstructions.fieldBody")} <br />
                {i18n.t("messagesAPIInstructions.fieldUserId")} <br />
                {i18n.t("messagesAPIInstructions.fieldQueueId")} <br />
                {i18n.t("messagesAPIInstructions.fieldSendSignature")} <br />
                {i18n.t("messagesAPIInstructions.fieldCloseTicket")} <br />
                {"}"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.elementMargin}>
                <b>{i18n.t("messagesAPIInstructions.testSend")}</b>
              </Typography>
              {renderFormMessageText()}
            </Grid>
          </Grid>
          <Typography
            variant="h6"
            color="primary"
            className={classes.sectionTitle}
          >
            {i18n.t("messagesAPI.API.media.title")}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography component="div" className={classes.elementMargin}>
                <p>{i18n.t("messagesAPI.API.media.instructions")}</p>
                <b>{i18n.t("messagesAPIInstructions.endpoint")}</b> {getEndpoint()} <br />
                <b>{i18n.t("messagesAPIInstructions.method")}</b> POST <br />
                <b>{i18n.t("messagesAPIInstructions.headers")}</b> {i18n.t("messagesAPIInstructions.headerAuthorizationMedia")} <br />
                <b>{i18n.t("messagesAPIInstructions.formData")}</b> <br />
                <ul className={classes.list}>
                  <li>
                    <b>number: </b> {i18n.t("messagesAPIInstructions.exampleNumber")}
                  </li>
                  <li>
                    <b>body:</b> {i18n.t("messagesAPIInstructions.docBodyFinal")}
                  </li>
                  <li>
                    <b>userId:</b> {i18n.t("messagesAPIInstructions.docUserIdFinal")} ""
                  </li>
                  <li>
                    <b>queueId:</b> {i18n.t("messagesAPIInstructions.docQueueIdFinal")} ""
                  </li>
                  <li>
                    <b>medias: </b> {i18n.t("messagesAPI.mediaMessage.media")}
                  </li>
                  <li>
                    <b>sendSignature:</b>{" "}
                    {i18n.t("messagesAPIInstructions.singMessage")} <br />
                  </li>
                  <li>
                    <b>closeTicket:</b>{" "}
                    {i18n.t("messagesAPIInstructions.closeTicket")} <br />
                  </li>
                </ul>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.elementMargin}>
                <b>{i18n.t("messagesAPIInstructions.testSend")}</b>
              </Typography>
              {renderFormMessageMedia()}
            </Grid>
          </Grid>
        </div>
      </Paper>
    </MainContainer>
  );
};
export default MessagesAPI;
